import { openImageQueueEventSource } from '../queue-sse.js';
import { createReconnectingEventSource } from '../interaction/ui/reconnecting-event-source.js';

export function renderImageManagerQueueItem(item, data, {
  statusLabel,
  statusClass,
  progress,
  position,
  progressMessage,
} = {}) {
  const main = document.createElement('div');
  main.className = 'im-queue-main';
  const title = document.createElement('div');
  title.className = 'im-queue-title';
  title.textContent = data.listingTitle || data.asin || '图片生成任务';
  const detail = document.createElement('div');
  detail.className = 'im-queue-detail';
  if (data.userDisplayName) {
    const owner = document.createElement('span');
    owner.className = 'im-owner-badge im-queue-owner';
    owner.textContent = `👤 ${data.userDisplayName}`;
    detail.appendChild(owner);
  }
  detail.append(`${statusLabel}${progress ? ` · ${progress}` : ''}${position}`);
  if (data.provider || data.model) detail.append(` · ${data.provider || 'unknown'}${data.model ? `/${data.model}` : ''}`);
  if (progressMessage) detail.append(document.createElement('br'), document.createTextNode(progressMessage));
  main.append(title, detail);
  const badge = document.createElement('span');
  badge.className = `im-queue-badge im-queue-badge-${statusClass}`;
  badge.textContent = statusLabel;
  item.replaceChildren(main, badge);
}

/**
 * Owns the image queue transport lifecycle. Refreshing grouped image data stays
 * injected because the page owns selection and expanded-card restoration.
 */
export function createImageManagerQueueController({
  selectById = (id) => document.getElementById(id),
  documentRef = document,
  isViewActive = () => false,
  scheduleRefresh = () => {},
  renderQueueItem = renderImageManagerQueueItem,
  pollIntervalMs = 10000,
  scheduleInterval = globalThis.setInterval,
  cancelInterval = globalThis.clearInterval,
  scheduleTimeout = globalThis.setTimeout,
  createEventSource = createReconnectingEventSource,
  openEventSource = openImageQueueEventSource,
} = {}) {
  let queueSSEController = null;
  let pollTimer = null;
  let queueActive = false;

  function syncQueueActiveFlag() {
    const list = selectById('im-queue-list');
    queueActive = Boolean(list && list.children.length > 0);
  }

  function stopPolling() {
    if (pollTimer) {
      cancelInterval(pollTimer);
      pollTimer = null;
    }
    queueActive = false;
  }

  function startPolling() {
    stopPolling();
    pollTimer = scheduleInterval(() => {
      if (!isViewActive()) return;
      syncQueueActiveFlag();
      if (!queueActive) return;
      scheduleRefresh();
    }, pollIntervalMs);
  }

  function ensureConnection() {
    if (queueSSEController?.isActive()) return;
    if (!queueSSEController) {
      queueSSEController = createEventSource({
        open: () => openEventSource(),
        onMessage: (event) => {
          try {
            handleEvent(JSON.parse(event.data));
          } catch { /* ignore malformed queue event */ }
        },
      });
    }
    queueSSEController.start();
  }

  function cleanup() {
    queueSSEController?.stop();
  }

  function handleEvent(data = {}) {
    if (data.type === 'connected' || data.type === 'queue-stats') return;

    const section = selectById('im-queue-section');
    const list = selectById('im-queue-list');
    if (!section || !list) return;

    section.hidden = false;
    let item = list.querySelector(`[data-task-id="${data.taskId}"]`);
    if (!item) {
      item = documentRef.createElement('div');
      item.className = 'im-queue-item';
      item.dataset.taskId = data.taskId;
      list.appendChild(item);
    }

    const statusLabels = {
      'task-queued': '排队中',
      'task-started': '生成中',
      'task-progress': '生成中',
      'slot-done': '生成中',
      'task-done': '已完成',
      'task-failed': '失败',
    };
    const statusClass = data.type === 'task-done' ? 'done'
      : data.type === 'task-failed' ? 'failed'
        : data.type === 'task-queued' ? 'waiting' : 'running';
    const progress = data.totalSlots ? `${data.completedSlots || 0}/${data.totalSlots}` : '';
    const position = data.queuePosition ? ` 前方 ${data.queuePosition} 位` : '';
    const progressMessage = data.message || data.task?.progress?.message || '';
    renderQueueItem(item, data, {
      statusLabel: statusLabels[data.type] || data.type,
      statusClass,
      progress,
      position,
      progressMessage,
    });

    if (['task-queued', 'task-started', 'task-progress', 'slot-done'].includes(data.type)) {
      queueActive = true;
    }
    if (data.type === 'slot-done' || data.type === 'task-done') {
      scheduleRefresh();
    }
    if (data.type === 'task-done' || data.type === 'task-failed') {
      scheduleTimeout(() => {
        item.remove();
        syncQueueActiveFlag();
        if (!queueActive) stopPolling();
      }, 10000);
    }
  }

  return Object.freeze({
    ensureConnection,
    cleanup,
    startPolling,
    stopPolling,
    handleEvent,
    isQueueActive: () => queueActive,
  });
}
